<template>
  <Dialog
    :visible="visible"
    @update:visible="() => $emit('update:visible', false)"
    modal
    :closable="false"
    :style="{ width: '50rem' }">
    <template #header>
      <div class="flex w-full justify-end space-x-4">
        <Button
          severity="secondary"
          label="Cancel"
          outlined
          @click="() => $emit('update:visible', false)" />
        <Button
          :label="`${dirtyPhrase.id ? 'Save' : 'Add'} Personalization`"
          @click="save()" />
      </div>
    </template>
    <div class="space-y-6">
      <FormGroup
        label="Depending on someone matching into the following Segment Group...">
        <Select
          v-model="dirtyPhrase.segmentGroupId"
          :options="segmentGroups"
          optionValue="id"
          optionLabel="name"
          class="w-full"></Select>
      </FormGroup>

      <FormGroup
        v-if="dirtyPhrase.segmentGroupId"
        label="Show the following if they don't match to any segments:">
        <InputText
          v-model="dirtyPhrase.fallbackText"
          class="w-full"
          size="large" />
      </FormGroup>

      <FormGroup
        v-if="dirtyPhrase.segmentGroupId && segmentGroup"
        label="Otherwise show a segment-specific personalization:">
        <Message
          v-if="segmentGroup.isMultiWinning"
          severity="info"
          :closable="false"
          class="mb-4">
          <template #icon>
            <i class="pi pi-info-circle" />
          </template>
          <div class="text-sm">
            <strong>Note:</strong> This segment group allows multiple selections. When a visitor matches multiple segments,
            the <strong>first matching personalization</strong> in the list below will be shown. The order matters - segments
            higher in the list take priority over those below.
          </div>
        </Message>
        <div class="grid grid-cols-2 ring-2 ring-primary-600/10">
          <template
            v-for="(segment, idx) in segmentGroup.segments"
            :key="segment.id">
            <div
              class="flex items-center py-6 pl-6"
              :class="{ 'bg-primary-600/10': idx % 2 }">
              {{ stripHtml(segmentName(segment)) }}
            </div>
            <div
              class="flex items-center pr-6"
              :class="{ 'bg-primary-600/10': idx % 2 }">
              <InputText
                v-model="dirtyPhrase.map[segment.id]"
                :placeholder="dirtyPhrase.fallbackText"
                class="w-full"
                size="large" />
            </div>
          </template>
        </div>
        <template #description>
          <div class="mt-4 flex justify-end">
            <Button
              label="AI Generate"
              severity="ai"
              icon="pi pi-box"
              size="small"
              :loading="isGenerating"
              :disabled="isGenerating"
              @click="generate" />
          </div>
        </template>
      </FormGroup>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { Phrase } from '@/types/config'

const emit = defineEmits(['save', 'update:visible'])
const props = defineProps({
  surroundingContent: String,
  visible: Boolean,
  fallbackText: String,
  phraseId: String
})

watch(
  () => props.visible,
  () => {
    if (props.fallbackText && !dirtyPhrase.value.id) {
      dirtyPhrase.value.fallbackText = props.fallbackText
    }
  }
)

const segmentGroupStore = useSegmentGroupStore()
const { segmentGroups } = storeToRefs(segmentGroupStore)

const phraseStore = usePhraseStore()

const dirtyPhrase = ref<Partial<Phrase>>({
  fallbackText: props.fallbackText,
  segmentGroupId: null,
  map: {}
})

const phrase = computed(() => {
  if (!props.phraseId) return null
  return phraseStore.getById(props.phraseId) || {}
})

onMounted(() => {
  if (phrase.value) {
    dirtyPhrase.value = { ...phrase.value }
  }
})

const segmentGroup = computed(() => {
  return segmentGroupStore.getById(dirtyPhrase.value.segmentGroupId)
})

const { generatePersonalizationTokens } = useRightyHeadless()
const questionStore = useQuestionStore()
const isGenerating = ref(false)

const generate = async () => {
  if (!segmentGroup.value || !dirtyPhrase.value?.fallbackText || isGenerating.value) return

  isGenerating.value = true

  try {
    const question = questionStore.getBySegmentGroupId(segmentGroup.value.id)

    const segments = segmentGroup.value.segments.map((s) => ({
      id: s.id,
      name: stripHtml(segmentName(s))
    }))

    const response = await generatePersonalizationTokens(
      stripHtml(renderText(props.surroundingContent || '', 10000)),
      question ? stripHtml(renderText(question.text)) : '',
      dirtyPhrase.value?.fallbackText,
      segments
    )

    if (response && typeof response === 'object') {
      // Response keys are segment IDs — map directly
      dirtyPhrase.value.map = {}

      segmentGroup.value.segments.forEach((segment) => {
        dirtyPhrase.value.map[segment.id] = response[segment.id] || dirtyPhrase.value.fallbackText
      })
    }
  } catch (error) {
    console.error('Error generating personalization tokens:', error)
  } finally {
    isGenerating.value = false
  }
}

const save = () => {
  if (!segmentGroup.value?.segments.length === 0) {
    alert('Please select a Segment Group or Question with segment options')
    return
  }
  dirtyPhrase.value.map = Object.fromEntries(
    segmentGroup.value.segments.map((segment) => [
      segment.id,
      dirtyPhrase.value.map[segment.id]
    ])
  )

  const existingPhrase = !!dirtyPhrase.value.id

  const newPhrase = phraseStore.upsert(dirtyPhrase.value)

  dirtyPhrase.value = { ...newPhrase }

  emit('save', newPhrase)

  if (!existingPhrase) {
    dirtyPhrase.value = {
      segmentGroupId: null,
      fallbackText: '',
      map: {}
    }
  }
}
</script>
